// Import models
import type {
  AbortAssetUploadResponseContent,
  AttachTaskRequestContent,
  AttachTaskResponseContent,
  BadRequestErrorResponseContent,
  CatalogTaskRequestContent,
  CatalogTaskResponseContent,
  CompleteAssetUploadRequestContent,
  CompleteAssetUploadResponseContent,
  CompleteMilestoneResponseContent,
  CreateProjectResponseContent,
  DelegateGoogleClassroomAccessRequestContent,
  DelegateGoogleClassroomAccessResponseContent,
  DelegateRobloxSecretStoreAccessRequestContent,
  DelegateRobloxSecretStoreAccessResponseContent,
  GetAssetUploadPartUrlResponseContent,
  GetGoogleClassroomAccessStatusResponseContent,
  GetLinkedRobloxAccountResponseContent,
  GetRobloxSecretStoreAccessStatusResponseContent,
  GetSimulatedTaskResponseContent,
  GetTaskCatalogForProjectResponseContent,
  GetTaskCatalogResponseContent,
  GetTaskResponseContent,
  GradeTaskRequestContent,
  GradeTaskResponseContent,
  InternalFailureErrorResponseContent,
  LinkRobloxAccountRequestContent,
  LinkRobloxAccountResponseContent,
  ListAssetsResponseContent,
  ListProjectsResponseContent,
  ListTasksForPlayerResponseContent,
  MissingRequiredRobloxOAuthScopesResponseContent,
  NotAuthorizedErrorResponseContent,
  NotFoundErrorResponseContent,
  PlaceIdProvidedErrorResponseContent,
  ProjectAlreadyExistsErrorResponseContent,
  PutExperienceRequestContent,
  PutExperienceResponseContent,
  RobloxLinkRequiredErrorResponseContent,
  SecretAlreadyExistsErrorResponseContent,
  StartAssetUploadRequestContent,
  StartAssetUploadResponseContent,
  VendSecretResponseContent,
} from '../models';
// Import request parameter interfaces
import {
    AbortAssetUploadRequest,
    AttachTaskRequest,
    CatalogTaskRequest,
    CompleteAssetUploadRequest,
    CompleteMilestoneRequest,
    CreateProjectRequest,
    DelegateGoogleClassroomAccessRequest,
    DelegateRobloxSecretStoreAccessRequest,
    GetAssetUploadPartUrlRequest,
    GetRobloxSecretStoreAccessStatusRequest,
    GetSimulatedTaskRequest,
    GetTaskRequest,
    GetTaskCatalogForProjectRequest,
    GradeTaskRequest,
    LinkRobloxAccountRequest,
    ListAssetsRequest,
    ListTasksForPlayerRequest,
    PutExperienceRequest,
    StartAssetUploadRequest,
    VendSecretRequest,
} from '..';

import { ResponseError } from '../runtime';
import { DefaultApi } from './DefaultApi';
import { DefaultApiDefaultContext } from "./DefaultApiClientProvider";

import {
    useQuery,
    UseQueryResult,
    UseQueryOptions,
    useInfiniteQuery,
    UseInfiniteQueryResult,
    UseInfiniteQueryOptions,
    useMutation,
    UseMutationOptions,
    UseMutationResult
} from "@tanstack/react-query";
import { createContext, useContext } from "react";

/**
 * Context for the API client used by the hooks.
 */
export const DefaultApiClientContext = createContext<DefaultApi | undefined>(undefined);

const NO_API_ERROR = new Error(`DefaultApi client missing. Please ensure you have instantiated the DefaultApiClientProvider with a client instance.`);

/**
 * useMutation hook for the AbortAssetUpload operation
 */
export const useAbortAssetUpload = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<AbortAssetUploadResponseContent, TError, AbortAssetUploadRequest>, 'mutationFn'>
): UseMutationResult<AbortAssetUploadResponseContent, TError, AbortAssetUploadRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: AbortAssetUploadRequest) => api.abortAssetUpload(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the AttachTask operation
 */
export const useAttachTask = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<AttachTaskResponseContent, TError, AttachTaskRequest>, 'mutationFn'>
): UseMutationResult<AttachTaskResponseContent, TError, AttachTaskRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: AttachTaskRequest) => api.attachTask(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the CatalogTask operation
 */
export const useCatalogTask = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<CatalogTaskResponseContent, TError, CatalogTaskRequest>, 'mutationFn'>
): UseMutationResult<CatalogTaskResponseContent, TError, CatalogTaskRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: CatalogTaskRequest) => api.catalogTask(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the CompleteAssetUpload operation
 */
export const useCompleteAssetUpload = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<CompleteAssetUploadResponseContent, TError, CompleteAssetUploadRequest>, 'mutationFn'>
): UseMutationResult<CompleteAssetUploadResponseContent, TError, CompleteAssetUploadRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: CompleteAssetUploadRequest) => api.completeAssetUpload(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the CompleteMilestone operation
 */
export const useCompleteMilestone = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<CompleteMilestoneResponseContent, TError, CompleteMilestoneRequest>, 'mutationFn'>
): UseMutationResult<CompleteMilestoneResponseContent, TError, CompleteMilestoneRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: CompleteMilestoneRequest) => api.completeMilestone(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the CreateProject operation
 */
export const useCreateProject = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<CreateProjectResponseContent, TError, CreateProjectRequest>, 'mutationFn'>
): UseMutationResult<CreateProjectResponseContent, TError, CreateProjectRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: CreateProjectRequest) => api.createProject(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the DelegateGoogleClassroomAccess operation
 */
export const useDelegateGoogleClassroomAccess = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<DelegateGoogleClassroomAccessResponseContent, TError, DelegateGoogleClassroomAccessRequest>, 'mutationFn'>
): UseMutationResult<DelegateGoogleClassroomAccessResponseContent, TError, DelegateGoogleClassroomAccessRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: DelegateGoogleClassroomAccessRequest) => api.delegateGoogleClassroomAccess(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the DelegateRobloxSecretStoreAccess operation
 */
export const useDelegateRobloxSecretStoreAccess = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<DelegateRobloxSecretStoreAccessResponseContent, TError, DelegateRobloxSecretStoreAccessRequest>, 'mutationFn'>
): UseMutationResult<DelegateRobloxSecretStoreAccessResponseContent, TError, DelegateRobloxSecretStoreAccessRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: DelegateRobloxSecretStoreAccessRequest) => api.delegateRobloxSecretStoreAccess(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the GetAssetUploadPartUrl operation
 */
export const useGetAssetUploadPartUrl = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<GetAssetUploadPartUrlResponseContent, TError, GetAssetUploadPartUrlRequest>, 'mutationFn'>
): UseMutationResult<GetAssetUploadPartUrlResponseContent, TError, GetAssetUploadPartUrlRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: GetAssetUploadPartUrlRequest) => api.getAssetUploadPartUrl(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetGoogleClassroomAccessStatus operation
 */
export const useGetGoogleClassroomAccessStatus = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<GetGoogleClassroomAccessStatusResponseContent, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetGoogleClassroomAccessStatusResponseContent, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getGoogleClassroomAccessStatus"], () => api.getGoogleClassroomAccessStatus(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetLinkedRobloxAccount operation
 */
export const useGetLinkedRobloxAccount = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<GetLinkedRobloxAccountResponseContent, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetLinkedRobloxAccountResponseContent, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getLinkedRobloxAccount"], () => api.getLinkedRobloxAccount(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetRobloxSecretStoreAccessStatus operation
 */
export const useGetRobloxSecretStoreAccessStatus = <TError = ResponseError>(
    params: GetRobloxSecretStoreAccessStatusRequest,
    options?: Omit<UseQueryOptions<GetRobloxSecretStoreAccessStatusResponseContent, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetRobloxSecretStoreAccessStatusResponseContent, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getRobloxSecretStoreAccessStatus", params], () => api.getRobloxSecretStoreAccessStatus(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetSimulatedTask operation
 */
export const useGetSimulatedTask = <TError = ResponseError>(
    params: GetSimulatedTaskRequest,
    options?: Omit<UseQueryOptions<GetSimulatedTaskResponseContent, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetSimulatedTaskResponseContent, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getSimulatedTask", params], () => api.getSimulatedTask(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetTask operation
 */
export const useGetTask = <TError = ResponseError>(
    params: GetTaskRequest,
    options?: Omit<UseQueryOptions<GetTaskResponseContent, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetTaskResponseContent, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getTask", params], () => api.getTask(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetTaskCatalog operation
 */
export const useGetTaskCatalog = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<GetTaskCatalogResponseContent, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetTaskCatalogResponseContent, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getTaskCatalog"], () => api.getTaskCatalog(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetTaskCatalogForProject operation
 */
export const useGetTaskCatalogForProject = <TError = ResponseError>(
    params: GetTaskCatalogForProjectRequest,
    options?: Omit<UseQueryOptions<GetTaskCatalogForProjectResponseContent, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetTaskCatalogForProjectResponseContent, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getTaskCatalogForProject", params], () => api.getTaskCatalogForProject(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the GradeTask operation
 */
export const useGradeTask = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<GradeTaskResponseContent, TError, GradeTaskRequest>, 'mutationFn'>
): UseMutationResult<GradeTaskResponseContent, TError, GradeTaskRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: GradeTaskRequest) => api.gradeTask(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the LinkRobloxAccount operation
 */
export const useLinkRobloxAccount = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<LinkRobloxAccountResponseContent, TError, LinkRobloxAccountRequest>, 'mutationFn'>
): UseMutationResult<LinkRobloxAccountResponseContent, TError, LinkRobloxAccountRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: LinkRobloxAccountRequest) => api.linkRobloxAccount(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the ListAssets operation
 */
export const useListAssets = <TError = ResponseError>(
    params: ListAssetsRequest,
    options?: Omit<UseQueryOptions<ListAssetsResponseContent, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<ListAssetsResponseContent, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["listAssets", params], () => api.listAssets(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the ListProjects operation
 */
export const useListProjects = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<ListProjectsResponseContent, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<ListProjectsResponseContent, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["listProjects"], () => api.listProjects(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the ListTasksForPlayer operation
 */
export const useListTasksForPlayer = <TError = ResponseError>(
    params: ListTasksForPlayerRequest,
    options?: Omit<UseQueryOptions<ListTasksForPlayerResponseContent, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<ListTasksForPlayerResponseContent, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["listTasksForPlayer", params], () => api.listTasksForPlayer(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the PutExperience operation
 */
export const usePutExperience = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<PutExperienceResponseContent, TError, PutExperienceRequest>, 'mutationFn'>
): UseMutationResult<PutExperienceResponseContent, TError, PutExperienceRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: PutExperienceRequest) => api.putExperience(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the StartAssetUpload operation
 */
export const useStartAssetUpload = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<StartAssetUploadResponseContent, TError, StartAssetUploadRequest>, 'mutationFn'>
): UseMutationResult<StartAssetUploadResponseContent, TError, StartAssetUploadRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: StartAssetUploadRequest) => api.startAssetUpload(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the VendSecret operation
 */
export const useVendSecret = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<VendSecretResponseContent, TError, VendSecretRequest>, 'mutationFn'>
): UseMutationResult<VendSecretResponseContent, TError, VendSecretRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: VendSecretRequest) => api.vendSecret(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

